import { useMemo } from 'react';
import type { PrevBrandItem } from '@/api/catBrand';
import { useTranslation } from '@/hooks/useTranslation';
import { PREV_BRAND_OTHERS } from '@/lib/prevBrandStorage';

const rowBtn =
  'flex w-full items-center justify-between border-b border-black/35 bg-transparent px-2 py-1.5 text-left text-white transition hover:bg-black/15';

const variantBtn =
  'w-full border-b border-black/30 px-2 py-1.5 text-left text-[14px] transition sm:text-[15px]';

const inputClass =
  'min-w-0 w-full rounded-sm border border-slate-800 bg-white px-2 py-2 text-[15px] text-slate-900 outline-none focus:border-slate-600 sm:text-[16px]';

type PrevBrandPickerProps = {
  brands: PrevBrandItem[];
  loading: boolean;
  expandedBrand: string | null;
  selectedBrand: string;
  selectedVariant: string;
  onExpandedBrandChange: (brand: string | null) => void;
  onSelectVariant: (brand: string, variant: string) => void;
  onOtherSpecifyChange: (value: string) => void;
};

export default function PrevBrandPicker({
  brands,
  loading,
  expandedBrand,
  selectedBrand,
  selectedVariant,
  onExpandedBrandChange,
  onSelectVariant,
  onOtherSpecifyChange,
}: PrevBrandPickerProps) {
  const { t } = useTranslation();

  const variantsForExpanded = useMemo(
    () => brands.find((b) => b.brand === expandedBrand)?.variants ?? [],
    [brands, expandedBrand]
  );

  const otherSpecifyValue = selectedBrand === PREV_BRAND_OTHERS ? selectedVariant : '';

  if (loading) {
    return <p className="m-0 text-white/90">{t.prevBrand.loading}</p>;
  }

  return (
    <div className="flex flex-col gap-1">
      {brands.map((item) => {
        const open = expandedBrand === item.brand;
        return (
          <div key={item.brand} className="bg-transparent">
            <button
              type="button"
              className={rowBtn}
              onClick={() => onExpandedBrandChange(open ? null : item.brand)}
              aria-expanded={open}
            >
              <span className="font-semibold">{item.brand}</span>
              <span className="text-lg leading-none">{open ? '▾' : '▸'}</span>
            </button>
            {open ? (
              <div className="ml-2 mt-1 grid grid-cols-1 gap-1">
                {variantsForExpanded.map((variant) => {
                  const selected = selectedBrand === item.brand && selectedVariant === variant;
                  return (
                    <button
                      key={variant}
                      type="button"
                      className={`${variantBtn} ${
                        selected ? 'bg-white/20 text-white' : 'bg-transparent text-white/95'
                      }`}
                      onClick={() => onSelectVariant(item.brand, variant)}
                    >
                      {variant}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>
        );
      })}

      <div className="mt-2 flex items-center gap-2 border-t border-black/35 pt-2">
        <label
          htmlFor="prev-brand-other-specify"
          className="shrink-0 text-left text-[15px] font-semibold text-white sm:text-[16px]"
        >
          {t.prevBrand.otherSpecify}
        </label>
        <input
          id="prev-brand-other-specify"
          type="text"
          value={otherSpecifyValue}
          onChange={(e) => onOtherSpecifyChange(e.target.value)}
          onFocus={() => {
            if (selectedBrand !== PREV_BRAND_OTHERS) {
              onOtherSpecifyChange('');
            }
          }}
          className={`${inputClass} min-w-0 flex-1`}
        />
      </div>
    </div>
  );
}

export function formatPrevBrandSelection(
  brand: string,
  variant: string,
  otherSpecifyLabel: string
): string {
  if (brand === PREV_BRAND_OTHERS) {
    return `${otherSpecifyLabel} ${variant}`;
  }
  return `${brand} - ${variant}`;
}

export function isPrevBrandSelectionValid(brand: string, variant: string): boolean {
  if (!brand.trim()) return false;
  if (brand === PREV_BRAND_OTHERS) return !!variant.trim();
  return !!variant.trim();
}
